import {
  HttpRequestEntity,
  HttpRequestModelType,
  HttpRequestStatus,
} from 'shared/domain/httpRequest/httpRequestModel';
import { HttpError } from 'serviceWorker/helpers/httpError';
import { NOT_FOUND } from 'shared/contants';
import {
  broadcastDocumentationDeletedError,
  broadcastDocumentationMovedError,
} from 'serviceWorker/broadcasts/documentations';
import {
  broadcastUploadedDocumentError,
} from 'serviceWorker/broadcasts/documents';
import {
  broadcastCreatedIssueError,
  broadcastDeletedIssueError,
  broadcastUpdatedIssueError,
} from 'serviceWorker/broadcasts/issues';
import {
  archiveDirection,
  isIssueRestoreRequest,
  restoreDirection,
} from 'serviceWorker/services/issues/changeIssueArchival';
import { issueRequestDataOf } from '../issue/issueRequestData';
import { broadcastUploadedLevelError } from 'serviceWorker/broadcasts/levels';
import { broadcastCreateReportError } from 'serviceWorker/broadcasts/reports';
import { broadcastUploadedSiteError } from 'serviceWorker/broadcasts/sites';
import { broadcastUploadedVisibleFieldError } from 'serviceWorker/broadcasts/visibleFields';
import { broadcastInspectionTemplateUploadError } from 'serviceWorker/broadcasts/inspectionTemplates';
import {
  broadcastUpdatedProtocolItemError,
  broadcastUpdatedProtocolSectionError,
  broadcastUploadedInspectionError,
} from 'serviceWorker/broadcasts/inspections';
import { debugLog } from 'shared/logger/debugLog';
import { neverReached } from 'shared/utils/neverReached';
import { chooseErrorMessage } from 'shared/utils/errorHandling';
import { broadcastCompanyError } from '../responseHandlers/company';
import { broadcastProjectError } from '../responseHandlers/project';
import { broadcastUserError } from '../responseHandlers/user';
import { broadcastContractError } from '../responseHandlers/contract';
import { broadcastOrganizationError } from '../responseHandlers/organization';
import { broadcastDocumentationError } from './documentation';
import { broadcastDocumentationVersionError } from './documentationVersion';
import {
  broadcastWorkflowError,
  broadcastStageError,
  broadcastDecisionError,
} from 'serviceWorker/broadcasts/workflow/error';
import { broadcastProcessError } from 'serviceWorker/broadcasts/processes';
import { broadcastChecklistAnswerOperationError } from 'serviceWorker/broadcasts/checklistAnswers';
import { broadcastChecklistSectionOperationError } from 'serviceWorker/broadcasts/checklistSections';
import { broadcastUpdatedProtocolAnswerError } from 'serviceWorker/broadcasts/protocolAnswer';

export async function emitErrorOnProperChannel(
  request: HttpRequestEntity,
  error: unknown,
  uniqueId?: string
): Promise<void> {
  switch (request.entityType) {
    case HttpRequestModelType.issue: {
      return broadcastIssueError(request, error);
    }
    case HttpRequestModelType.drawing:
    case HttpRequestModelType.document: {
      return broadcastDocumentError(request, error);
    }
    case HttpRequestModelType.report: {
      return broadcastReportError(request, error, uniqueId);
    }
    case HttpRequestModelType.documentation: {
      return broadcastDocumentationError(request, error);
    }
    case HttpRequestModelType.documentationVersion: {
      return broadcastDocumentationVersionError(request, error);
    }
    case HttpRequestModelType.documentationMove: {
      return broadcastMoveDocumentationError(request, error);
    }
    case HttpRequestModelType.documentationDelete: {
      return broadcastDeleteDocumentationError(request, error);
    }
    case HttpRequestModelType.documentationRestore: {
      return broadcastRestoreDocumentationError(request, error);
    }
    case HttpRequestModelType.level: {
      return broadcastLevelError(request, error, uniqueId);
    }
    case HttpRequestModelType.site: {
      return broadcastSiteError(request, error, uniqueId);
    }
    case HttpRequestModelType.directory: {
      return;
    }
    case HttpRequestModelType.visibleField: {
      return broadcastVisibleFieldError(request, error, uniqueId);
    }
    case HttpRequestModelType.project: {
      return broadcastProjectError(request, error, uniqueId);
    }
    case HttpRequestModelType.organization: {
      return broadcastOrganizationError(request, error, uniqueId);
    }
    case HttpRequestModelType.company: {
      return broadcastCompanyError(request, error, uniqueId);
    }
    case HttpRequestModelType.user: {
      return broadcastUserError(request, error, uniqueId);
    }
    case HttpRequestModelType.contract: {
      return broadcastContractError(request, error, uniqueId);
    }
    case HttpRequestModelType.inspectionTemplate: {
      return broadcastInspectionTemplateError(request, error);
    }
    case HttpRequestModelType.inspection: {
      return broadcastInspectionError(request, error);
    }
    case HttpRequestModelType.protocolItem: {
      return broadcastProtocolItemError(request, error);
    }
    case HttpRequestModelType.protocolSection: {
      return broadcastProtocolSectionError(request, error);
    }
    case HttpRequestModelType.protocolAnswer:
    case HttpRequestModelType.protocolSectionAnswersBatch: {
      broadcastUpdatedProtocolAnswerError(error, request.data?.uniqueId);
      return;
    }
    case HttpRequestModelType.checklistSection:
    case HttpRequestModelType.checklistSectionDocument: {
      return broadcastChecklistSectionHttpError(request, error);
    }
    case HttpRequestModelType.checklistAnswer: {
      return broadcastChecklistAnswerHttpError(request, error);
    }
    case HttpRequestModelType.workflow: {
      return broadcastWorkflowEntityError(request, error);
    }
    case HttpRequestModelType.workflowStage:
    case HttpRequestModelType.workflowStageReorder: {
      return broadcastStageEntityError(request, error);
    }
    case HttpRequestModelType.workflowDecision: {
      return broadcastDecisionEntityError(request, error);
    }
    case HttpRequestModelType.process: {
      return broadcastProcessEntityError(request, error);
    }
    default:
      // ensure every case is handled when we add new type.
      neverReached(request.entityType as never);
  }
}

function broadcastDocumentError(
  request: HttpRequestEntity,
  error: unknown
): void {
  debugLog('document error', error);
  const messageId = chooseErrorMessage(error);
  broadcastUploadedDocumentError(error, messageId);
}

function broadcastReportError(
  request: HttpRequestEntity,
  error: unknown,
  uniqueId?: string
): void {
  if (request.status === HttpRequestStatus.FAILED) {
    broadcastCreateReportError(
      error,
      uniqueId ?? request.data?.uniqueId,
      request.data
    );
  }
}

/** Carries the change's `uniqueId`, so the page's command rejects with this error. */
function broadcastLevelError(
  request: HttpRequestEntity,
  error: unknown,
  uniqueId?: string
): void {
  broadcastUploadedLevelError(error, chooseErrorMessage(error), uniqueId ?? request.data?.uniqueId);
}

/** Carries the change's `uniqueId`, so the page's command rejects with this error. */
function broadcastSiteError(
  request: HttpRequestEntity,
  error: unknown,
  uniqueId?: string
): void {
  broadcastUploadedSiteError(error, chooseErrorMessage(error), uniqueId ?? request.data?.uniqueId);
}

function broadcastMoveDocumentationError(
  request: HttpRequestEntity,
  error: any
): void {
  if (request.method === 'PUT') {
    broadcastDocumentationMovedError(error);
  }
}

function broadcastDeleteDocumentationError(
  request: HttpRequestEntity,
  error: any
): void {
  if (request.method === 'DELETE') {
    broadcastDocumentationDeletedError(error);
  }
}

function broadcastRestoreDocumentationError(
  request: HttpRequestEntity,
  error: any
): void {
  if (request.method === 'GET') {
    broadcastDocumentationDeletedError(error);
  }
}

/**
 * The page reads the issue again after the error: the local half of the
 * refused change (archive or restore) is undone first.
 */
function undoLocallyThen(
  undoLocally: (issueId: string) => Promise<unknown>,
  issueId: string | undefined,
  broadcast: () => void
): void {
  const undone = issueId ? undoLocally(issueId) : Promise.resolve();
  void undone.catch(() => undefined).then(broadcast);
}

/** A failed issue change is answered with the request's `uniqueId`; a refused archive or restore is undone locally. */
export function broadcastIssueError(
  request: HttpRequestEntity,
  error: unknown
): void {
  const messageId = chooseErrorMessage(error);
  const { uniqueId, issueId } = issueRequestDataOf(request);
  if (isIssueRestoreRequest(request)) {
    return undoLocallyThen(restoreDirection.undoLocally, issueId, () =>
      broadcastUpdatedIssueError(error, messageId, uniqueId)
    );
  }
  switch (request.method) {
    case 'POST':
      return broadcastCreatedIssueError(error, messageId, uniqueId);
    case 'DELETE':
      return undoLocallyThen(archiveDirection.undoLocally, issueId, () =>
        broadcastDeletedIssueError(error, messageId, uniqueId)
      );
    default:
      return broadcastUpdatedIssueError(error, messageId, uniqueId);
  }
}

function broadcastVisibleFieldError(
  request: HttpRequestEntity,
  error: unknown,
  uniqueId?: string
): void {
  broadcastUploadedVisibleFieldError(
    error,
    chooseErrorMessage(error),
    uniqueId ?? request.data?.uniqueId
  );
}


function broadcastInspectionTemplateError(
  request: HttpRequestEntity,
  error: unknown
): void {
  broadcastInspectionTemplateUploadError(error, request.data?.uniqueId);
}

function broadcastInspectionError(
  request: HttpRequestEntity,
  error: unknown
): void {
  const messageId = chooseErrorMessage(error);
  broadcastUploadedInspectionError(error, request.data?.uniqueId, messageId);
}

function broadcastProtocolItemError(
  request: HttpRequestEntity,
  error: unknown
): void {
  broadcastUpdatedProtocolItemError(error, request.data?.uniqueId);
}

function broadcastProtocolSectionError(
  request: HttpRequestEntity,
  error: unknown
): void {
  // A protocol section that no longer exists server-side (e.g. the template
  // was restructured) is not an actionable error for the user - the queue
  // silently drops these requests (see resolveSkipOrRemove), so don't toast.
  if (error instanceof HttpError && error.status === NOT_FOUND) {
    return;
  }

  broadcastUpdatedProtocolSectionError(error, request.data?.uniqueId);
}

function broadcastWorkflowEntityError(
  request: HttpRequestEntity,
  error: unknown
): void {
  const messageId = chooseErrorMessage(error);
  broadcastWorkflowError(error, request.data?.uniqueId, messageId);
}

function broadcastStageEntityError(
  request: HttpRequestEntity,
  error: unknown
): void {
  const messageId = chooseErrorMessage(error);
  broadcastStageError(error, request.data?.uniqueId, messageId);
}

function broadcastDecisionEntityError(
  request: HttpRequestEntity,
  error: unknown
): void {
  const messageId = chooseErrorMessage(error);
  broadcastDecisionError(error, request.data?.uniqueId, messageId);
}

function broadcastProcessEntityError(
  request: HttpRequestEntity,
  error: unknown
): void {
  const messageId = chooseErrorMessage(error);
  broadcastProcessError(error, request.data?.uniqueId, messageId ?? undefined);
}

function broadcastChecklistSectionHttpError(
  request: HttpRequestEntity,
  error: unknown
): void {
  const messageId = chooseErrorMessage(error);
  broadcastChecklistSectionOperationError(
    error,
    request.data?.uniqueId,
    messageId
  );
}

function broadcastChecklistAnswerHttpError(
  request: HttpRequestEntity,
  error: unknown
): void {
  const messageId = chooseErrorMessage(error);
  broadcastChecklistAnswerOperationError(
    error,
    request.data?.uniqueId,
    messageId
  );
}
